/**
 * authToken — Auth-Hook für das Frontend (Token, Session, Login/Register/Logout).
 *
 * Hängt ein gespeichertes JWT (falls vorhanden) an jeden axios-Request. Ohne Token
 * bleibt im Bestands-/Single-Tenant-Modus alles wie bisher (Backend-Owner-Fallback).
 *
 * SaaS-Modus: Ist REACT_APP_REQUIRE_AUTH=true gesetzt, erzwingt das Frontend einen
 * Login (siehe ProtectedRoute in App.js) und leitet bei abgelaufener Session (401)
 * automatisch zur Anmeldung.
 */
import axios from "axios";

const TOKEN_KEY = "ff_auth_token";
const USER_KEY = "ff_auth_user";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Ist Login erzwungen (SaaS-Modus)? Build-Flag REACT_APP_REQUIRE_AUTH=true. */
export const REQUIRE_AUTH =
  String(process.env.REACT_APP_REQUIRE_AUTH || "").toLowerCase() === "true";

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function isAuthenticated() {
  return !!getAuthToken();
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStoredUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage nicht verfügbar — ignorieren */
  }
}

export function clearAuthToken() {
  setAuthToken(null);
  setStoredUser(null);
}

// Feste Testphasen-Länge (Spiegel von trialConfig.days) — bewusst ohne Import, um
// eine Abhängigkeit der Auth-Lib auf die Pricing-Daten zu vermeiden.
const TRIAL_DAYS = 14;
const PLAN_KEY_LS = "foerderfuchs_current_plan";
const TRIAL_KEY_LS = "foerderfuchs_trial_started_at";

/**
 * Plan-/Trial-Zustand serverautoritativ aus GET /billing/plan in den localStorage
 * spiegeln. Ohne diese Hydration hängen die Feature-Gates nur am localStorage — ein
 * zahlender/Trial-Kunde sähe auf einem NEUEN Gerät alles gesperrt. Best-effort:
 * Fehler werden geschluckt (Gates fallen dann auf ihr Bestandsverhalten zurück).
 */
export async function hydratePlanFromServer() {
  try {
    const res = await axios.get(`${API}/billing/plan`);
    const d = res?.data || {};
    if (d.purchased && d.tier && d.tier !== "free") {
      localStorage.setItem(PLAN_KEY_LS, d.tier);
    }
    // Trial-Uhr geräteübergreifend korrekt setzen: Start = Ablauf − 14 Tage, damit
    // useTrialStatus die echten Resttage rechnet (kein Reset auf frische 14 Tage).
    if (d.trial_active && d.trial_ends_at) {
      const endMs = Date.parse(d.trial_ends_at);
      if (!Number.isNaN(endMs)) {
        localStorage.setItem(TRIAL_KEY_LS, String(endMs - TRIAL_DAYS * 86400 * 1000));
      }
    }
  } catch (_) { /* best-effort — Server-Gate bleibt maßgeblich */ }
}

/** Login → Token + User speichern. Gibt die Server-Antwort zurück.
 *  remember=true (Default): Backend stellt eine 30-Tage-Session aus („Angemeldet
 *  bleiben") — Kunden, die nur sporadisch reinschauen, bleiben eingeloggt. */
export async function login(email, password, remember = true) {
  const res = await axios.post(`${API}/auth/login`, { email, password, remember });
  if (res?.data?.access_token) setAuthToken(res.data.access_token);
  if (res?.data?.user) setStoredUser(res.data.user);
  await hydratePlanFromServer();   // Feature-Gates am echten Server-Tarif ausrichten
  return res?.data;
}

/** Registrierung (neuer Kunde, eigener Workspace) → Auto-Login. */
export async function register(email, password, workspaceName) {
  const res = await axios.post(`${API}/auth/register`, {
    email,
    password,
    workspace_name: workspaceName || undefined,
  });
  if (res?.data?.access_token) setAuthToken(res.data.access_token);
  if (res?.data?.user) setStoredUser(res.data.user);
  await hydratePlanFromServer();
  return res?.data;
}

/** Passwort-Reset anfordern (neutrale Antwort — verrät nie, ob das Konto existiert). */
export async function requestPasswordReset(email) {
  const res = await axios.post(`${API}/auth/forgot-password`, { email });
  return res?.data;
}

/** Passwort mit Reset-Token neu setzen. */
export async function resetPassword(token, password) {
  const res = await axios.post(`${API}/auth/reset-password`, { token, password });
  return res?.data;
}

/** Token verwerfen (stateless serverseitig). */
export async function logout() {
  try {
    await axios.post(`${API}/auth/logout`);
  } catch {
    /* serverseitig stateless — Fehler ignorierbar */
  }
  clearAuthToken();
}

let _installed = false;

/** Installiert einmalig die globalen Auth-Interceptoren (Bearer-Header + 401-Handling). */
export function installAuthInterceptor() {
  if (_installed) return;
  _installed = true;

  axios.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  // Im SaaS-Modus: abgelaufene/ungültige Session (401) → sauber ausloggen und zur
  // Anmeldung leiten, statt den Nutzer mit kryptischen Fehlern hängen zu lassen.
  // WICHTIG: nur wenn es überhaupt ein Token GAB. Anonyme Besucher auf öffentlichen
  // Seiten (Landing/Pricing/Recht) bekommen für einzelne API-Calls legitim 401 —
  // die dürfen nicht in den Login gebounct werden, sonst ist das Schaufenster kaputt.
  axios.interceptors.response.use(
    (res) => res,
    (error) => {
      const status = error?.response?.status;
      if (REQUIRE_AUTH && status === 401 && getAuthToken()) {
        clearAuthToken();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.assign(`/login?next=${next}`);
        }
      }
      return Promise.reject(error);
    }
  );
}
